import type { JSX, Signal } from '@builder.io/qwik';
import { useContext } from '@builder.io/qwik';
import { useVisibleTask$ } from '@builder.io/qwik';
import { useSignal, $ } from '@builder.io/qwik';
import { component$ } from '@builder.io/qwik';
import Arrow from '~/icons/arrow';
import { AppDataContext } from '~/contexts/AppDataContext';

interface ControlsType {
  showLeftArrow: Signal<boolean>;
  showRightArrow: Signal<boolean>;
  scrollContainer: Signal<Element | undefined>;
}

export default component$<ControlsType>(
  ({ showLeftArrow, showRightArrow, scrollContainer }): JSX.Element => {
    const {
      payloadToSSR: { isMobileViewer },
    } = useContext(AppDataContext);
    const maxScrollCount = useSignal(0);
    const scrollPosition = useSignal(0);
    const scrollWidthFactor = 0.5;

    // eslint-disable-next-line qwik/no-use-visible-task
    useVisibleTask$(() => {
      if (scrollContainer.value && !isMobileViewer) {
        // scrollLeft - current horizontal scroll position of the element where 0 means the leftmost position
        // scrollWidth - total width of the scrollable content inside the container, including overflow
        // clientWidth - visible width of the container
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;
        showLeftArrow.value = scrollLeft > 0;
        showRightArrow.value = scrollLeft + clientWidth < scrollWidth;

        // Calculate max number of scrolls to the right
        // (scrollWidth - clientWidth) - overflow width
        // (clientWidth * scrollWidthFactor) - half of the width of the container (assuming scrollWidthFactor of 0.5)
        maxScrollCount.value = Math.ceil(
          (scrollWidth - clientWidth) / (clientWidth * scrollWidthFactor)
        );
      }
    });

    const scroll = $((direction: 'left' | 'right') => {
      if (scrollContainer.value && !isMobileViewer) {
        // Scroll half the width of the container with each scroll navigation
        const scrollAmount =
          scrollContainer.value.clientWidth * scrollWidthFactor;

        // Do the actual scrolling - scrollBy is a built-in method for HTMLElement
        scrollContainer.value.scrollBy({
          left: direction === 'left' ? -scrollAmount : scrollAmount,
          behavior: 'smooth',
        });
        if (direction === 'left') {
          scrollPosition.value--;
        }
        if (direction === 'right') {
          scrollPosition.value++;
        }

        // If we're in position 0, we're scrolled all the way left
        showLeftArrow.value = scrollPosition.value > 0;

        // If we're scrolled to position maxScrollCount, we're scrolled all the way right
        showRightArrow.value = scrollPosition.value < maxScrollCount.value;
      }
    });

    return (
      <>
        {showLeftArrow.value && (
          <div class='absolute top-1/2 -translate-y-1/2 md:-left-10 z-10'>
            <button
              type='button'
              onClick$={() => scroll('left')}
              class='w-8 h-8 flex justify-center items-center'
            >
              <Arrow direction='left' />
            </button>
          </div>
        )}
        {showRightArrow.value && (
          <div class='absolute top-1/2 -translate-y-1/2 md:-right-10 z-10'>
            <button
              type='button'
              onClick$={() => scroll('right')}
              class='w-8 h-8 flex justify-center items-center'
            >
              <Arrow direction='right' />
            </button>
          </div>
        )}
      </>
    );
  }
);
